import { useState, useEffect, useCallback } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/contexts/AuthContext";
import { AppShell3 } from "@/components/app-shell-3";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageTransition } from "@/components/PageTransition";
import { ToolHeader } from "@/components/ToolHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
  MessageSquare,
  CreditCard,
  ShoppingCart,
  Settings,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Save,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { getSmsCredits, getSmsSendLog, getSmsCreditTransactions } from "@/lib/smsService";

export function SmsCreditsPage() {
  usePageTitle("SMS Credits");
  const { company } = useAuth();
  const navigate = useNavigate();

  const [credits, setCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(true);

  // Sender ID
  const [senderId, setSenderId] = useState("");
  const [savingSenderId, setSavingSenderId] = useState(false);

  // Message log
  const [sendLog, setSendLog] = useState([]);
  const [sendLogTotal, setSendLogTotal] = useState(0);
  const [sendLogPage, setSendLogPage] = useState(1);
  const [loadingSendLog, setLoadingSendLog] = useState(true);

  // Transaction history
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  const PAGE_SIZE = 15;

  const fetchCredits = useCallback(async () => {
    if (!company?.id) return;
    try {
      setLoadingCredits(true);
      const data = await getSmsCredits(company.id);
      setCredits(data);
    } catch {
      setCredits(null);
    } finally {
      setLoadingCredits(false);
    }
  }, [company?.id]);

  const fetchSendLog = useCallback(async () => {
    if (!company?.id) return;
    try {
      setLoadingSendLog(true);
      const result = await getSmsSendLog(company.id, { page: sendLogPage, pageSize: PAGE_SIZE });
      setSendLog(result.data);
      setSendLogTotal(result.total);
    } catch {
      setSendLog([]);
    } finally {
      setLoadingSendLog(false);
    }
  }, [company?.id, sendLogPage]);

  const fetchTransactions = useCallback(async () => {
    if (!company?.id) return;
    try {
      setLoadingTransactions(true);
      const result = await getSmsCreditTransactions(company.id, { page: transactionsPage, pageSize: PAGE_SIZE });
      setTransactions(result.data);
      setTransactionsTotal(result.total);
    } catch {
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [company?.id, transactionsPage]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  useEffect(() => {
    fetchSendLog();
  }, [fetchSendLog]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Initialize sender ID from company settings
  useEffect(() => {
    if (company?.settings?.sms_sender_id) {
      setSenderId(company.settings.sms_sender_id);
    }
  }, [company?.settings?.sms_sender_id]);

  const handleSaveSenderId = async () => {
    const trimmed = senderId.trim();
    if (trimmed && !/^[a-zA-Z0-9]{1,11}$/.test(trimmed)) {
      toast.error("Sender ID must be 1-11 alphanumeric characters");
      return;
    }

    try {
      setSavingSenderId(true);
      const currentSettings = company?.settings || {};
      const { error } = await supabase
        .from("companies")
        .update({ settings: { ...currentSettings, sms_sender_id: trimmed || "Training" } })
        .eq("id", company.id);

      if (error) throw error;
      toast.success("Sender ID updated");
    } catch (err) {
      toast.error(err.message || "Failed to update sender ID");
    } finally {
      setSavingSenderId(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusBadge = (status) => {
    switch (status) {
      case "sent":
      case "delivered":
        return <Badge className="bg-green-100 text-green-700 border-green-200">{status}</Badge>;
      case "failed":
        return <Badge variant="destructive">{status}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const typeBadge = (type) => {
    switch (type) {
      case "grant_free":
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Free Grant</Badge>;
      case "purchase":
        return <Badge className="bg-green-100 text-green-700 border-green-200">Purchase</Badge>;
      case "usage":
        return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Usage</Badge>;
      case "refund":
        return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Refund</Badge>;
      case "adjustment":
        return <Badge variant="secondary">Adjustment</Badge>;
      default:
        return <Badge variant="secondary">{type}</Badge>;
    }
  };

  const sendLogTotalPages = Math.ceil(sendLogTotal / PAGE_SIZE);
  const transactionsTotalPages = Math.ceil(transactionsTotal / PAGE_SIZE);

  if (loadingCredits) {
    return (
      <AppShell3>
        <div className="min-h-[60vh] flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName="Company Settings"
          toolName="SMS Credits"
          currentTool="sms"
          isGlobalView={true}
          parentBreadcrumb={{
            name: 'Company Settings',
            onClick: () => navigate('/company-settings')
          }}
        />
        <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">

          {/* Balance + Buy Credits Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Balance Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Available Credits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">{credits?.balance ?? 0}</span>
                  <span className="text-muted-foreground">credits</span>
                </div>
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                  <span>Free: {credits?.lifetime_free ?? 0}</span>
                  <span>Purchased: {credits?.lifetime_purchased ?? 0}</span>
                  <span>Used: {credits?.lifetime_used ?? 0}</span>
                </div>
              </CardContent>
            </Card>

            {/* Buy Credits Card (Coming Soon) */}
            <Card className="relative overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Buy Credits</CardDescription>
                  <Badge variant="secondary">Coming Soon</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <ShoppingCart className="h-8 w-8" />
                  <p className="text-sm">
                    Purchase additional SMS credits with Stripe. Available soon.
                  </p>
                </div>
              </CardContent>
              <div className="absolute inset-0 bg-background/50 pointer-events-none" />
            </Card>
          </div>

          {/* Sender ID Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Settings className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Sender ID</CardTitle>
                  <CardDescription>
                    Customize the sender name that appears on SMS messages
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-3">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="sender-id">Sender Name</Label>
                  <Input
                    id="sender-id"
                    placeholder="Training"
                    value={senderId}
                    onChange={(e) => setSenderId(e.target.value)}
                    maxLength={11}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground">
                    Max 11 alphanumeric characters. Messages will appear from: <strong>{senderId || "Training"}</strong>
                  </p>
                </div>
                <Button onClick={handleSaveSenderId} disabled={savingSenderId} size="sm">
                  {savingSenderId ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tabbed Log View */}
          <Tabs defaultValue="messages">
            <TabsList>
              <TabsTrigger value="messages">
                <MessageSquare className="h-4 w-4 mr-1.5" />
                Message Log
              </TabsTrigger>
              <TabsTrigger value="transactions">
                <CreditCard className="h-4 w-4 mr-1.5" />
                Transaction History
              </TabsTrigger>
            </TabsList>

            {/* Message Log */}
            <TabsContent value="messages">
              <Card>
                <CardContent className="p-0">
                  {loadingSendLog ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : sendLog.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <MessageSquare className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No messages sent yet</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left px-4 py-3 font-medium">Date</th>
                              <th className="text-left px-4 py-3 font-medium">Sent By</th>
                              <th className="text-left px-4 py-3 font-medium">Recipient</th>
                              <th className="text-left px-4 py-3 font-medium">Message</th>
                              <th className="text-center px-4 py-3 font-medium">Status</th>
                              <th className="text-center px-4 py-3 font-medium">Credits</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sendLog.map((log) => (
                              <tr key={log.id} className="border-b last:border-0 hover:bg-muted/30">
                                <td className="px-4 py-3 whitespace-nowrap text-xs">{formatDate(log.created_date)}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {log.sent_by_profile?.display_name || "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div>
                                    <span>{log.recipient_name || log.recipient_profile?.display_name || "-"}</span>
                                    <span className="text-xs text-muted-foreground block">{log.recipient_phone}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 max-w-xs truncate">{log.message_content}</td>
                                <td className="px-4 py-3 text-center">{statusBadge(log.status)}</td>
                                <td className="px-4 py-3 text-center">{log.credits_used}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {sendLogTotalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t">
                          <span className="text-xs text-muted-foreground">
                            Page {sendLogPage} of {sendLogTotalPages} ({sendLogTotal} messages)
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              disabled={sendLogPage <= 1}
                              onClick={() => setSendLogPage((p) => p - 1)}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              disabled={sendLogPage >= sendLogTotalPages}
                              onClick={() => setSendLogPage((p) => p + 1)}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transaction History */}
            <TabsContent value="transactions">
              <Card>
                <CardContent className="p-0">
                  {loadingTransactions ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <CreditCard className="h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">No transactions yet</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left px-4 py-3 font-medium">Date</th>
                              <th className="text-left px-4 py-3 font-medium">Type</th>
                              <th className="text-right px-4 py-3 font-medium">Amount</th>
                              <th className="text-right px-4 py-3 font-medium">Balance</th>
                              <th className="text-left px-4 py-3 font-medium">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((tx) => (
                              <tr key={tx.id} className="border-b last:border-0 hover:bg-muted/30">
                                <td className="px-4 py-3 whitespace-nowrap text-xs">{formatDate(tx.created_date)}</td>
                                <td className="px-4 py-3">{typeBadge(tx.type)}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  <span className={tx.amount > 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                    {tx.amount > 0 ? "+" : ""}{tx.amount}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">{tx.balance_after}</td>
                                <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{tx.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {transactionsTotalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t">
                          <span className="text-xs text-muted-foreground">
                            Page {transactionsPage} of {transactionsTotalPages} ({transactionsTotal} transactions)
                          </span>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              disabled={transactionsPage <= 1}
                              onClick={() => setTransactionsPage((p) => p - 1)}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              disabled={transactionsPage >= transactionsTotalPages}
                              onClick={() => setTransactionsPage((p) => p + 1)}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
